import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiAcuerdosGestores, CodigoLerControllerService, PoseedorControllerService } from 'api-rest';
import { SnackbarService } from '../../services/snackbar.service';
import { SpinnerService } from '../../services/spinner.service';

@Component({
  selector: 'managers-agreements-holders-relation-modal',
  templateUrl: './managers-agreements-holders-relation-modal.component.html',
  styleUrls: ['./managers-agreements-holders-relation-modal.component.scss']
})
export class ManagersAgreementsHoldersRelationModalComponent implements OnInit {
  formGroup!: FormGroup;
  codigosLerArray: any[] = [];
  poseedoresEncontrados: any[] = [];
  relacion: any;
  onlyView = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ManagersAgreementsHoldersRelationModalComponent>,
    private formBuilder: FormBuilder,
    public spinnerSrv: SpinnerService,
    private snackBarSrv: SnackbarService,
    private codigoLerService: CodigoLerControllerService,
    private poseedorControllerService: PoseedorControllerService,
    @Inject(ApiAcuerdosGestores.PoseedoresAcuerdoControllerService)
    private poseedoresAcuerdoControllerSrv: ApiAcuerdosGestores.PoseedoresAcuerdoControllerService,
    @Inject(ApiAcuerdosGestores.ConsentimientoControllerService)
    private consentimientoControllerService: ApiAcuerdosGestores.ConsentimientoControllerService,
  ) {
    this.formGroup = this.formBuilder.group({
      documento: [{ value: null, disabled: false }, [Validators.required]],
      poseedores: [{ value: [], disabled: false }, [Validators.required]],
      codigosLer: [{ value: [], disabled: false }, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.relacion = this.data?.relacion;
    this.onlyView = !!this.data?.onlyView;
    if (this.relacion?.poseedor) {
      this.poseedoresEncontrados = [this.relacion.poseedor];
      this.formGroup.get('poseedores')?.setValue([this.relacion.poseedor], { emitEvent: false });
      this.formGroup.get('documento')?.setValue(this.relacion?.poseedor?.codigoDocumento ?? null, { emitEvent: false });
    }
    this.loadCodigosLer();
  }

  buscarPoseedores(): void {
    const documento = (this.formGroup.get('documento')?.value ?? '').toString().trim().toUpperCase();
    if (!documento) {
      this.snackBarSrv.showSnackBar('Debe indicar un documento para buscar poseedores', 'error');
      return;
    }

    const filtro: any = {
      codigoDocumento: documento,
    };

    this.spinnerSrv.loadingShow();
    this.poseedorControllerService.findAll5(filtro, { page: 0, size: 200, sort: ['id,desc'] }).subscribe({
      next: (res: any) => {
        this.spinnerSrv.loadingHide();
        this.poseedoresEncontrados = res?.datos ?? [];
        this.formGroup.get('poseedores')?.setValue([], { emitEvent: false });
        if (!this.poseedoresEncontrados.length) {
          this.snackBarSrv.showSnackBar('No se han encontrado poseedores para el documento indicado', 'warning');
        }
      },
      error: () => {
        this.spinnerSrv.loadingHide();
        this.poseedoresEncontrados = [];
        this.snackBarSrv.showSnackBar('Ha ocurrido un error al buscar poseedores', 'error');
      },
    });
  }

  getPoseedorLabel(poseedor: any): string {
    const razonSocial = poseedor?.razonSocial ?? poseedor?.poseedor?.razonSocial;
    const nima = poseedor?.nima ?? poseedor?.poseedor?.nima;
    const centro = poseedor?.denominacionCentro ?? poseedor?.poseedor?.denominacionCentro;
    return [razonSocial, nima, centro].filter(Boolean).join(' - ');
  }

  compareById(a: any, b: any): boolean {
    if (!a || !b) {
      return a === b;
    }
    return a.id === b.id;
  }

  loadCodigosLer(): void {
    this.codigoLerService.findAll4({ acuerdoGestores: true }, { page: 0, size: 1000 }).subscribe({
      next: (res: any) => {
        this.codigosLerArray = res?.datos ?? [];
        const selected = (this.relacion?.codigoLer ?? []).map((x: any) => x.id);
        this.formGroup.get('codigosLer')?.setValue(selected);
        if (this.onlyView) {
          this.formGroup.disable({ emitEvent: false });
        }
      },
      error: () => {
        this.codigosLerArray = [];
      },
    });
  }

  canDeleteCodLer(): boolean {
    const estadoDesc = this.relacion?.estado?.descripcion ?? '';
    return estadoDesc.toLowerCase().includes('pdte de generar');
  }

  removeCodLer(id: number): void {
    if (this.onlyView || !this.canDeleteCodLer()) {
      return;
    }
    const current = this.formGroup.get('codigosLer')?.value ?? [];
    this.formGroup.get('codigosLer')?.setValue(current.filter((item: number) => item !== id));
  }

  getLerById(id: number): any {
    return this.codigosLerArray.find((item: any) => item.id === id);
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  guardar(): void {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      this.snackBarSrv.showSnackBar('Debe completar los campos obligatorios', 'error');
      return;
    }

    const selectedIds: number[] = this.formGroup.get('codigosLer')?.value ?? [];
    const selectedLer = this.codigosLerArray.filter((item: any) => selectedIds.includes(item.id));
    const poseedoresSeleccionados: any[] = this.formGroup.get('poseedores')?.value ?? [];

    if (!this.relacion?.id) {
      if (!poseedoresSeleccionados.length) {
        this.snackBarSrv.showSnackBar('Debe seleccionar al menos un poseedor', 'error');
        return;
      }

      if (!this.data?.acuerdoIndirectoInfo?.id) {
        this.snackBarSrv.showSnackBar('No se pudo identificar el acuerdo para crear el consentimiento', 'error');
        return;
      }

      const payload: ApiAcuerdosGestores.ConsentimientoCreatePayloadDTO = {
        acuerdoIndirecto: { id: this.data?.acuerdoIndirectoInfo?.id } as any,
        poseedores: poseedoresSeleccionados,
        codigoLer: selectedLer,
        codigoDocumento: this.formGroup.get('documento')?.value ?? null,
      } as any;

      this.spinnerSrv.loadingShow();
      this.consentimientoControllerService.create2(payload).subscribe({
        next: () => {
          this.spinnerSrv.loadingHide();
          this.snackBarSrv.showSnackBar('Consentimiento creado correctamente', 'success');
          this.dialogRef.close({ saved: true });
        },
        error: () => {
          this.spinnerSrv.loadingHide();
          this.snackBarSrv.showSnackBar('Ha ocurrido un error al crear el consentimiento', 'error');
        },
      });

      return;
    }

    const updatePayload = {
      ...this.relacion,
      codigoLer: selectedLer,
    };

    this.spinnerSrv.loadingShow();
    this.poseedoresAcuerdoControllerSrv.updateById(this.relacion.id, updatePayload).subscribe({
      next: () => {
        this.spinnerSrv.loadingHide();
        this.snackBarSrv.showSnackBar('Relación actualizada correctamente', 'success');
        this.dialogRef.close({ saved: true });
      },
      error: () => {
        this.spinnerSrv.loadingHide();
        this.snackBarSrv.showSnackBar('Ha ocurrido un error al actualizar la relación', 'error');
      },
    });
  }
}
